import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, Minus, Plus, Check, ArrowRight, ExternalLink } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { swatchFor } from '../lib/colors';
import { flyToCart } from '../lib/flyToCart';
import Magnetic from './motion/Magnetic';
import { addRecentlyViewed } from '../lib/recentlyViewed';
import ShadeCompare from './ShadeCompare';

// Peek full product details from the Shop grid without leaving the page.
const QuickView = ({ product, open, onClose }) => {
  const { addItem } = useCart();
  const [length, setLength] = useState(product.lengths?.[0]);
  const [color, setColor] = useState(product.colors?.[0]);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    if (open) {
      addRecentlyViewed(product);
      setLength(product.lengths?.[0]);
      setColor(product.colors?.[0]);
      setQty(1);
      setActiveImg(0);
      setAdded(false);
    }
  }, [open, product]);

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const colorImages = product.color_images || {};
  const colorSet = product.color_galleries?.[color];
  const gallery = (colorSet && colorSet.length)
    ? colorSet
    : (colorImages[color] ? [colorImages[color], ...(product.gallery || [product.image])] : (product.gallery?.length ? product.gallery : [product.image]));
  const idx = Math.min(activeImg, gallery.length - 1);
  const fitClass = product.image_fit === 'contain' ? 'object-contain' : 'object-cover';
  const unitPrice = product.price_by_length?.[length] ?? product.price;

  const handleAdd = (e) => {
    stop(e);
    flyToCart(document.querySelector('[data-testid="quickview-main-image"]'), product.image);
    addItem(product, { length, color, quantity: qty, price: unitPrice });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-[#0b0b0b]/70 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={(e) => { stop(e); onClose(); }}
          data-testid="quickview-overlay"
        >
          <motion.div
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-[#faf6ef] rounded-xl shadow-2xl"
            initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            onClick={stop}
            data-testid="quickview-modal"
          >
            <button onClick={(e) => { stop(e); onClose(); }} data-testid="quickview-close"
              className="absolute top-4 right-4 z-10 bg-[#0b0b0b]/80 hover:bg-[#0b0b0b] text-[#e7d3a1] p-2 rounded-full"><X size={18} /></button>

            <div className="grid sm:grid-cols-2 gap-6 p-5 sm:p-6">
              {/* Gallery */}
              <div>
                <div className="bg-[#ece5d8] aspect-[3/4] overflow-hidden rounded-lg">
                  <img src={gallery[idx]} alt={product.name} data-testid="quickview-main-image" className={`w-full h-full ${fitClass}`} />
                </div>
                {gallery.length > 1 && (
                  <div className="flex gap-2 mt-3">
                    {gallery.slice(0, 5).map((g, i) => (
                      <button key={i} onClick={(e) => { stop(e); setActiveImg(i); }}
                        className={`w-14 h-16 overflow-hidden rounded border ${idx === i ? 'border-[#c6a15b]' : 'border-transparent'}`}>
                        <img src={g} alt="" className={`w-full h-full ${fitClass}`} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex flex-col">
                {product.bestseller && <span className="inline-block self-start bg-[#0b0b0b] text-[#e7d3a1] overline text-[0.55rem] px-3 py-1.5 mb-3">Bestseller</span>}
                <h2 className="font-display text-3xl text-[#1a1a1a] leading-tight">{product.name}</h2>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={13} className={i < Math.round(product.rating) ? 'fill-[#c6a15b] text-[#c6a15b]' : 'text-[#d8cdb8]'} />)}</div>
                  <span className="font-body text-xs text-[#6b6459]">{product.rating} ({product.reviews})</span>
                </div>
                <p className="font-display text-2xl text-[#a8823c] mt-3">${unitPrice.toFixed(2)}{product.price_by_length ? <span className="font-body text-xs text-[#6b6459] ml-2">· {length}</span> : ''}</p>
                {product.description && <p className="font-body text-sm text-[#4a463d] leading-relaxed mt-3 line-clamp-4">{product.description}</p>}

                {product.lengths?.length > 0 && (
                  <div className="mt-4">
                    <p className="overline text-[#1a1a1a] text-[0.6rem] mb-2">Length — <span className="text-[#a8823c]">{length}</span></p>
                    <div className="flex flex-wrap gap-1.5">
                      {product.lengths.map((l) => (
                        <button key={l} onClick={(e) => { stop(e); setLength(l); }}
                          className={`min-w-[46px] px-3 py-2 font-body text-xs border transition-colors ${length === l ? 'bg-[#0b0b0b] text-[#e7d3a1] border-[#0b0b0b]' : 'border-[#d8cdb8] text-[#4a463d] hover:border-[#c6a15b]'}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                )}

                {product.colors?.length > 0 && (
                  <div className="mt-4">
                    <p className="overline text-[#1a1a1a] text-[0.6rem] mb-2">Color — <span className="text-[#a8823c]">{color}</span></p>
                    <div className="flex flex-wrap gap-2">
                      {product.colors.map((c) => (
                        <button key={c} onClick={(e) => { stop(e); setColor(c); setActiveImg(0); }} title={c}
                          className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${color === c ? 'border-[#c6a15b] ring-2 ring-[#c6a15b]/40' : 'border-black/15'}`}
                          style={{ background: swatchFor(c) }} />
                      ))}
                    </div>
                  </div>
                )}

                {product.colors?.length > 1 && (product.color_images || product.color_galleries) && (
                  <div className="mt-3">
                    <ShadeCompare
                      colors={product.colors}
                      variant="card"
                      imageFor={(c) => product.color_galleries?.[c]?.[0] || product.color_images?.[c]}
                      onChoose={(c) => { setColor(c); setActiveImg(0); }}
                      lengths={product.lengths || []}
                      selectedLength={length}
                    />
                  </div>
                )}

                {product.external_url ? (
                  <div className="mt-5">
                  <Magnetic className="block">
                    <a href={product.external_url} target="_blank" rel="noreferrer" data-testid="quickview-buy-website"
                      onClick={(e) => e.stopPropagation()}
                      className="btn-gold btn-shimmer btn-attention w-full py-3.5 flex items-center justify-center gap-2">
                      <span className="flex items-center gap-2">Buy From Website <ExternalLink size={15} /></span>
                    </a>
                  </Magnetic>
                  <p className="font-body text-[0.65rem] italic text-[#a8a091] mt-2" data-testid="quickview-bl-disclaimer">*Listed price may differ from Bask &amp; Lather website. Check Bask &amp; Lather website for current pricing.</p>
                  </div>
                ) : (
                <div className="flex items-stretch gap-3 mt-5">
                  <div className="flex items-center border border-[#d8cdb8]">
                    <button onClick={(e) => { stop(e); setQty((q) => Math.max(1, q - 1)); }} className="px-3 text-[#6b6459] hover:text-[#0b0b0b]"><Minus size={14} /></button>
                    <span className="px-3 font-body text-sm">{qty}</span>
                    <button onClick={(e) => { stop(e); setQty((q) => q + 1); }} className="px-3 text-[#6b6459] hover:text-[#0b0b0b]"><Plus size={14} /></button>
                  </div>
                  <Magnetic className="flex-1">
                    <button onClick={handleAdd} data-testid="quickview-add" className="btn-gold btn-shimmer btn-attention w-full py-3.5 flex items-center justify-center gap-2">
                      <span className="flex items-center gap-2">{added ? <><Check size={15} /> Added</> : 'Add To Bag'}</span>
                    </button>
                  </Magnetic>
                </div>
                )}

                <Link to={`/product/${product.id}`} onClick={(e) => e.stopPropagation()} data-testid="quickview-full-link"
                  className="mt-4 inline-flex items-center gap-1.5 font-body text-xs text-[#a8823c] hover:text-[#7d5f22] transition-colors self-start">
                  View full details <ArrowRight size={13} />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default QuickView;
